import { ToolbarItems } from '@fluentsms/editors';
import { Button, makeStyles, Theme } from '@material-ui/core';
import React, { ReactNode, useRef } from 'react';
import RichTextMergeFieldEditor from '../RichTextMergeFieldEditor';
import { Mentionable } from './Mentionable';
import cuid from 'cuid';
import type { MentionableProps } from './Mentionable';
import NavigationPrompt from '../NavigationPrompt';
import { useRouteMatch } from 'react-router-dom';

interface Props {
  value: string;
  mentionableProps?: null | Omit<MentionableProps, 'target'>;
  text: {
    submit: ReactNode;
    cancel: ReactNode;
  };
  testIds: {
    submitButton: string;
    cancelButton: string;
  };
  handlers: {
    onChange(body: string): void;
    onSubmit(): unknown;
    onCancel(): unknown;
  };
}

const CUSTOM_TOOLBAR_CONTENTS = [
  ToolbarItems.BOLD,
  ToolbarItems.ITALIC,
  ToolbarItems.UNDERLINE,
  ToolbarItems.STRIKETHROUGH,
  ToolbarItems.ALIGNMENTS,
  ToolbarItems.ORDEREDLIST,
  ToolbarItems.UNORDEREDLIST,
  ToolbarItems.CREATELINK,
  ToolbarItems.UNDO,
  ToolbarItems.REDO,
];

const TEXT_EDITOR_HEIGHT = 200;

const useStyles = makeStyles((theme: Theme) => ({
  buttonsContainer: {
    marginTop: theme.spacing(2),
    textTransform: 'uppercase',
    [theme.breakpoints.only('xs')]: {
      minHeight: 'auto',
      flexDirection: 'column',
      display: 'flex',
      paddingBottom: 0,
    },
    [theme.breakpoints.up('sm')]: {
      '& > :first-child': {
        marginLeft: theme.spacing(2),
      },
      display: 'flex',
      flexDirection: 'row-reverse',
    },
  },
}));

export function CommentEditor({ value, text, testIds, handlers, mentionableProps = null }: Props) {
  const { url } = useRouteMatch();
  const editorId = cuid();
  const classes = useStyles();
  const originalValue = useRef(value);
  const hasChanged = value.trim() !== originalValue.current.trim();

  function handleChange(raw: string, html: string) {
    const isEmpty = raw.trim() === '';
    return handlers.onChange(isEmpty ? '' : html);
  }

  return (
    <>
      <RichTextMergeFieldEditor
        editorId={editorId}
        autoFocus={true}
        enableTabKey={true}
        customToolbar={true}
        customToolbarContents={CUSTOM_TOOLBAR_CONTENTS}
        height={TEXT_EDITOR_HEIGHT}
        textContent={value}
        onChange={handleChange}
        pasteCleanupSettings={{
          allowedStyleProps: [],
          deniedAttrs: [],
          deniedTags: [],
          keepFormat: false,
          plainText: true,
          prompt: false,
        }}
      />
      <div className={classes.buttonsContainer}>
        <Button
          onClick={handlers.onSubmit}
          color="primary"
          size="small"
          disabled={!hasChanged}
          data-testid={testIds.submitButton}
        >
          {text.submit}
        </Button>
        <Button
          onClick={handlers.onCancel}
          color="secondary"
          variant="outlined"
          size="small"
          data-testid={testIds.cancelButton}
        >
          {text.cancel}
        </Button>
      </div>
      {mentionableProps && (
        <div>
          <Mentionable
            target={`#${editorId}_rte-edit-view`}
            users={mentionableProps.users}
            status={mentionableProps.status}
            onMentionUser={mentionableProps.onMentionUser}
            onUnmentionUser={mentionableProps.onUnmentionUser}
          />
        </div>
      )}
      <NavigationPrompt currentUrl={url} allowNavigation={!hasChanged} />
    </>
  );
}
